import { useMemo } from 'react';

import { extractTrimmedValues } from 'ff/debrief/utils';
import {
    useCurrentDebrief,
    useDebriefSelector,
    useRouteContext,
} from 'ff/debrief/hooks';
import {
    useGetDebriefByTrackUUIDQuery,
    useGetTracklogByIdQuery,
} from 'ff/debrief/features/TracklogApi';
import DebriefSegments, {
    SegmentNode,
} from 'ff/debrief/features/DebriefSegments';

export const useDebriefSegments = () => {
    const { selectedTracklogId } = useRouteContext();
    const { isScoreModeEnabled } = useCurrentDebrief();
    const selectedSegment = useDebriefSelector(s => s.appSlice.selectedSegment);

    const { data: debrief, ...debriefQueryStatus } =
        useGetDebriefByTrackUUIDQuery(selectedTracklogId, {
            skip: !selectedTracklogId,
        });

    const { data: tracklog, ...tracklogQueryStatus } = useGetTracklogByIdQuery(
        selectedTracklogId,
        {
            skip: !selectedTracklogId,
        }
    );

    const tracklogData = extractTrimmedValues(tracklog);

    const tree = useMemo(() => {
        if (
            !debriefQueryStatus.isSuccess ||
            !tracklogQueryStatus.isSuccess ||
            debrief?.debriefSegments === undefined ||
            debrief?.debriefSegments.length === 0
        )
            return null;
        return new DebriefSegments({
            rootSegment: debrief.debriefSegments[0],
            trackPoints: tracklog.points.data,
            startTime: Math.floor(tracklogData.timestampStart),
            endTime:
                Math.floor(tracklogData.timestampStart) +
                tracklogData.totalDurationSeconds,
            isScoreModeEnabled: isScoreModeEnabled,
        });
    }, [
        debrief?.analysisTimeUtc,
        debrief?.trackUuid,
        tracklog?.trackUuid,
        isScoreModeEnabled,
    ]);

    const selectedSegmentPoints: SegmentNode = useMemo(() => {
        if (selectedSegment && tree) {
            const seg = tree.getSegmentById(selectedSegment.id);
            return seg?.label === 'Entire Flight' ? null : seg;
        } else {
            return null;
        }
    }, [
        tracklogQueryStatus.isSuccess,
        selectedSegment?.id,
        debrief?.analysisTimeUtc,
    ]);

    return {
        selectedSegment: selectedSegmentPoints,
        segmentTree: tree,
    };
};
